<template>
  <div class="documents-input">
    <div
      v-show="shouldShowAddButton"
      :class="addButtonClass"
      class="documents-input__add-button"
      @click="handleClick"
      @dragend="handleDragEnd"
      @dragover="handleDragOver"
      @drop="handleDrop"
    >
      <input
        ref="input"
        :accept="accept"
        :multiple="multiple"
        class="documents-input__input"
        type="file"
        @change="handleChange"
      >
      <font-awesome-icon
        class="documents-input__add-button-icon"
        size="6x"
        icon="upload"
      />
      <span v-t="'c.documentsInput.form.label.add'" />

    </div>
    <div
      v-for="(preview, index) in previews"
      v-if="preview && preview.src"
      :key="index"
      class="documents-input__preview"
    >
      <div
        v-if="isImage(preview)"
        :style="previewStyle(preview)"
        class="documents-input__image-preview"
      />
      <div
        v-if="!isImage(preview)"
        class="documents-input__generic-preview"
      >
        <font-awesome-icon
          class="documents-input__add-button-icon"
          size="6x"
          icon="file-alt"
        />
        {{ preview.originalFilename }}
      </div>
      <button
        class="documents-input__remove-preview"
        @click.prevent="handleRemove(preview)"
      >
        <font-awesome-icon icon="times" />
      </button>
    </div>
  </div>
</template>

<script>
import clone from 'just-clone'
import isBlank from 'is-blank'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

export default {
  name: 'DocumentsInput',
  components: {
    FontAwesomeIcon
  },
  props: {
    accept: {
      type: String,
      default: () => '*'
    },
    multiple: {
      type: Boolean,
      default: () => null
    },
    value: {
      type: [Object, Array],
      required: true
    },
    removeMethod: {
      type: Function,
      default: () => null
    }
  },
  data () {
    return {
      isDragging: false
    }
  },
  computed: {
    previews () {
      const items = this.multiple
        ? this.value
        : [this.value]

      return items.filter(a => !a.markedForDeletion)
    },
    previewStyle () {
      return preview => `background-image: url(${preview.src});`
    },
    shouldShowAddButton () {
      const { multiple, value } = this
      return multiple || isBlank(this.value)
    },
    addButtonClass () {
      return {
        'documents-input__add-button--dragging': this.isDragging
      }
    },
    isImage () {
      return preview => preview.contentType.startsWith('image/')
    }
  },
  methods: {
    handleChange (event) {
      const files = event.target.files || event.dataTransfer.files
      if (isBlank(files) || isBlank(files.length)) {
        return
      }
      return this.readFiles(files)
        .then(() => { this.$refs.input.value = null })
    },
    handleClick () {
      this.$refs.input.click()
    },
    handleDragOver (event) {
      event.preventDefault()
      this.isDragging = true
    },
    handleDragEnd (event) {
      event.preventDefault()
      this.isDragging = false
    },
    handleDrop (event) {
      event.preventDefault()
      this.readFiles(event.dataTransfer.files)
    },
    readFiles (files) {
      // converts FileList to Array
      const filesArray = [...files]
      const readPromises = filesArray.map(file => this.readFile(file))

      return Promise.all(readPromises)
        .then(readFiles => {
          let appended = clone(this.value)

          if (this.multiple) {
            readFiles.forEach(file => appended.push(file))
          } else {
            appended.push(readFiles[0])
          }

          this.$emit('input', appended)
        })
    },
    readFile (file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.onload = event => {
          resolve({
            contentType: file.type,
            file,
            originalFilename: file.name,
            src: event.target.result
          })
        }
        fileReader.onerror = error => reject(error)
        fileReader.readAsDataURL(file)
      })
    },
    handleRemove (preview) {
      const { $emit, removeMethod, multiple, value } = this
      if (removeMethod) {
        removeMethod(preview)
      } else {
        if (multiple) {
          $emit('input', value.filter(f => f !== preview))
        } else {
          $emit('input', null)
        }
      }
    }
  }
}
</script>

<style lang="sass">
@import '~styles/imports'
@import '~styles/forms/mixins'

.documents-input
  display: flex
  flex-wrap: wrap
  justify-content: space-between

.documents-input__input
  +visuallyhidden

.documents-input__add-button,
.documents-input__generic-preview
  align-items: center
  border-radius: 3px
  border: 1px solid $color-border-1
  color: $color-form-label-1
  cursor: pointer
  display: flex
  flex-direction: column
  justify-content: center
  padding: 0
  text-align: center
  user-select: none

.documents-input__add-button,
.documents-input__preview,
  margin-top: rem-calc(30)
  min-height: rem-calc(200)
  position: relative
  width: 48%

.documents-input__generic-preview
  +ellipsis
  min-height: rem-calc(200)
  padding: rem-calc(55) rem-calc(20) rem-calc(0)

.documents-input__image-preview
  background-color: $color-grey-1
  background-position: center center
  background-repeat: no-repeat
  background-size: contain
  height: 100%
  width: 100%

.documents-input__add-button-icon
  display: block
  height: rem-calc(38)
  margin: 10px auto 10px
  width: rem-calc(100)

.documents-input__remove-preview
  background-color: #f00
  border-radius: rem-calc(100)
  color: #fff
  height: rem-calc(32)
  padding-top: rem-calc(1)
  position: absolute
  right: rem-calc(8)
  top: rem-calc(8)
  width: rem-calc(32)
</style>



// WEBPACK FOOTER //
// src/components/forms/DocumentsInput.vue